import {
  canonicalSources,
  EDIT_PAGE_LIST_KEYS,
  normalizeWebsiteUrl,
  type BrandInformationSaveRequest,
  type BrandProfileView,
  type BrandSourceTag,
  type EditPageListKey,
} from '@mg/shared';

/**
 * The Brand information page's form and its save request (web.md §6.2 with final-design §2.14):
 * the workspace name and website, and the five Vocabulary lists and Writing guidelines. Every other
 * brand field is read-only on the page and changes only in Brand Setup (spec:174-177), so it is never
 * part of the form, never part of the request, and the server refuses it (BRAND_READ_ONLY_FIELD).
 * Pure: the page's hook keeps the state, these decide what it means.
 */

/** What the page lets an admin change. */
export interface BrandInformationForm {
  name: string;
  /** As typed (the box rewrites a valid address to its `https://` form when it is left). */
  websiteUrl: string;
  lists: Record<EditPageListKey, string[]>;
}

/** The form as a view holds it: the values the page starts from, and returns to on Discard. */
export function brandInformationFormOf(view: BrandProfileView): BrandInformationForm {
  return {
    name: view.workspace.name,
    websiteUrl: view.workspace.websiteUrl ?? '',
    lists: listsOf(view),
  };
}

/** Copies of the editable lists, exactly as stored (grandfathered items kept: E4). */
export function listsOf(view: BrandProfileView): Record<EditPageListKey, string[]> {
  const lists = {} as Record<EditPageListKey, string[]>;
  for (const key of EDIT_PAGE_LIST_KEYS) lists[key] = [...view.fields[key]];
  return lists;
}

/** Same items in the same order. Typing in an "Add…" box is not a change until the item is added (spec:197). */
export function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((item, i) => item === b[i]);
}

/** The editable lists that differ from what `base` holds, in Edit-page order. */
export function changedListKeys(form: BrandInformationForm, base: BrandProfileView): EditPageListKey[] {
  return EDIT_PAGE_LIST_KEYS.filter((key) => !sameList(form.lists[key], base.fields[key]));
}

/** The name differs from the saved one once trimmed (it is sent only then: it takes the install-wide name lock). */
export function nameChanged(form: BrandInformationForm, base: BrandProfileView): boolean {
  return form.name.trim() !== base.workspace.name;
}

/** The website differs from the saved one, trimmed. */
export function websiteChanged(form: BrandInformationForm, base: BrandProfileView): boolean {
  return form.websiteUrl.trim() !== (base.workspace.websiteUrl ?? '');
}

/** Something on the page would be saved: the name, the website or a list (spec:197). */
export function isBrandInformationDirty(form: BrandInformationForm, base: BrandProfileView): boolean {
  return nameChanged(form, base) || websiteChanged(form, base) || changedListKeys(form, base).length > 0;
}

/**
 * The PUT /tenants/:id/brand/information body, against the view the form was read from (`base`):
 * - `expectedVersion`: that view's version, so a save over a change made elsewhere is refused (409);
 * - `name`: only when it changed, trimmed;
 * - `websiteUrl`: always (O15: a stored address that is no longer valid must be fixed on save), in
 *   its normalized `https://` form, or as typed when it is not valid (the page does not save then);
 * - `lists`: only the lists that changed, values only (the server works out their source tags: a
 *   changed list gains Manual Entry), each exactly as the page holds it. Only the editable keys
 *   (EDIT_PAGE_LIST_KEYS) can ever be in it: never a read-only field.
 */
export function brandInfoPayload(form: BrandInformationForm, base: BrandProfileView): BrandInformationSaveRequest {
  const website = normalizeWebsiteUrl(form.websiteUrl);
  const payload: BrandInformationSaveRequest = {
    expectedVersion: base.version,
    websiteUrl: website.ok ? website.url : form.websiteUrl.trim(),
  };
  if (nameChanged(form, base)) payload.name = form.name.trim();
  const changed = changedListKeys(form, base);
  if (changed.length > 0) {
    const lists: Partial<Record<EditPageListKey, string[]>> = {};
    for (const key of changed) lists[key] = [...form.lists[key]];
    payload.lists = lists;
  }
  return payload;
}

/**
 * The source tags a list shows while it is edited, by the server's rule for a list saved from this
 * page (final-design §2.7 item 3): an empty list has none; a changed list keeps its stored tags and
 * gains Manual Entry; an unchanged one shows what is stored (spec:107, :109).
 */
export function listProvenance(
  form: BrandInformationForm,
  base: BrandProfileView,
  key: EditPageListKey,
): { sources: BrandSourceTag[]; aiSuggested: boolean } {
  const items = form.lists[key];
  if (items.length === 0) return { sources: [], aiSuggested: false };
  const stored = base.provenance[key];
  const storedSources = stored?.sources ?? [];
  const sources = sameList(items, base.fields[key])
    ? canonicalSources(storedSources)
    : canonicalSources([...storedSources, 'manualEntry']);
  return { sources, aiSuggested: stored?.aiSuggested ?? false };
}

/** Whether two views hold the same values for everything on the form (name, website, the lists). */
export function sameFormValues(a: BrandProfileView, b: BrandProfileView): boolean {
  return (
    a.workspace.name === b.workspace.name &&
    (a.workspace.websiteUrl ?? '') === (b.workspace.websiteUrl ?? '') &&
    EDIT_PAGE_LIST_KEYS.every((key) => sameList(a.fields[key], b.fields[key]))
  );
}
